import { Suspense, lazy, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError, radia, type MapaThreadu } from '../api/client.js';
import { Button, Card, Notice, SectionTitle } from '../components/ui.js';
import { IconRefresh } from '../components/icons.js';

/*
 * Mapa siete Thread; zobrazuje sa v obrazovke Matter.
 *
 * Patrí tam preto, že zariadenia, ktoré v tej sieti sú, sú zariadenia
 * Matteru. Rádiá zostávajú o hardvéri — kanály, prepínanie rádií
 * a meranie pásma (rozhodnutie zadávateľa 2026-10-16).
 *
 * Mapa stojí na NAŠOM zozname zariadení a na susedských tabuľkách, ktoré
 * uzly samé hlásia. Nestojí na zbierke border routera, a to z dvoch
 * dôvodov, oba zistené na hardvéri:
 *
 * - zbierka obsahuje naše zariadenia bez názvu, takže by boli v mape dva
 *   razy — raz ako adresa a raz ako naše zariadenie,
 * - jej `role` nesedí: o všetkých uzloch tvrdila `router`, kým samé
 *   zariadenia hlásili spiace a reed.
 *
 * Preto sa načíta hneď a nič netreba spúšťať.
 */

const PlatnoMapy = lazy(() => import('./thread-map.js'));

export function MapaSieteThread({ jeSpravca }: { readonly jeSpravca: boolean }): ReactNode {
  const [mapa, setMapa] = useState<MapaThreadu | null>(null);
  const [bezi, setBezi] = useState(false);
  const [chyba, setChyba] = useState<string | null>(null);
  /**
   * Border router nie je nastavený.
   *
   * Nie je to chyba — Thread sa zapína vedome. Preto to nesvieti
   * červeno a mapa o sieti netvrdí nič.
   */
  const [bezBorderRoutera, setBezBorderRoutera] = useState(false);
  const zruseny = useRef(false);

  useEffect(
    () => () => {
      zruseny.current = true;
    },
    [],
  );

  const nacitaj = useCallback(async (): Promise<void> => {
    setBezi(true);
    try {
      const m = await radia.mapaThreadu();
      if (!zruseny.current) {
        setMapa(m);
        setChyba(null);
      }
    } catch (e) {
      if (zruseny.current) return;
      if (e instanceof ApiError && e.status === 503) {
        setBezBorderRoutera(true);
        return;
      }
      setChyba(
        e instanceof ApiError && e.message.length > 0 ? e.message : 'Mapu sa nepodarilo načítať.',
      );
    } finally {
      if (!zruseny.current) setBezi(false);
    }
  }, []);

  useEffect(() => {
    void nacitaj();
  }, [nacitaj]);

  if (bezBorderRoutera) {
    return (
      <section className="flex min-w-0 flex-col gap-3">
        <SectionTitle>Mapa siete Thread</SectionTitle>
        <Card className="p-4">
          <p className="text-sm text-(--color-text-muted)">
            Adresa border routera nie je nastavená (<code>LOXLINK_OTBR_URL</code>), takže o sieti
            Thread brána netvrdí nič.
          </p>
        </Card>
      </section>
    );
  }

  const prazdna = mapa === null || mapa.uzly.length === 0;
  const bezSpoja = mapa?.bezSpoja.length ?? 0;

  return (
    <section className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle>Mapa siete Thread</SectionTitle>
        {jeSpravca && (
          <Button
            onClick={() => {
              void nacitaj();
            }}
            disabled={bezi}
            icon={<IconRefresh className="size-4" />}
          >
            {bezi ? 'Načítavam…' : 'Načítať znova'}
          </Button>
        )}
      </div>

      {chyba !== null && (
        <Notice tone="danger">
          <p>{chyba}</p>
        </Notice>
      )}

      {prazdna ? (
        <Card className="p-4">
          <p className="text-sm text-(--color-text-muted)">
            {bezi
              ? 'Načítavam…'
              : 'V sieti Thread nie je žiadne naše zariadenie, takže mapa nemá čo nakresliť.'}
          </p>
        </Card>
      ) : (
        <Card className="flex min-w-0 flex-col gap-3 p-4">
          <Suspense
            fallback={
              <div className="h-96 w-full rounded-(--radius-control) border border-(--color-border) bg-(--color-surface-sunken)" />
            }
          >
            <PlatnoMapy mapa={mapa} />
          </Suspense>

          <dl className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-(--color-text-subtle)">
            <div className="flex items-center gap-1.5">
              <span className="size-2.5 rotate-45 bg-(--color-accent)" />
              brána
            </div>
            <div className="flex items-center gap-1.5">
              <span className="size-2.5 rounded-full bg-(--color-ok)" />
              router
            </div>
            <div className="flex items-center gap-1.5">
              <span className="size-2.5 rounded-full border-2 border-(--color-border-strong)" />
              koncové alebo spiace
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-0.5 w-5 bg-(--color-ok)" />
              dobré spojenie
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-0.5 w-5 bg-(--color-danger)" />
              slabé spojenie
            </div>
          </dl>

          {/*
            Čestná výhrada. Čiary sú z toho, čo si uzol pamätá; keď
            suseda nehlási, čiara nie je — a nedokresľuje sa.
          */}
          <p className="text-xs text-(--color-text-muted)">
            Čiary sú z toho, s kým uzol naozaj komunikoval a akú to malo kvalitu — hlási si to sám v
            tabuľke susedov. Hrúbka a farba sú podľa kvality; po nabehnutí na čiaru uvidíš silu
            signálu v dBm. Spiace zariadenie sa ozve, až keď sa prebudí, takže jeho čiara môže
            chvíľu chýbať.
          </p>

          {bezSpoja > 0 && (
            <p className="text-xs text-(--color-text-muted)">
              {bezSpoja === 1
                ? 'Jedno zariadenie nehlási žiadneho suseda'
                : `${String(bezSpoja)} zariadenia nehlásia žiadneho suseda`}
              , takže je v mape bez čiary. Pri zariadení na batérii je to bežné — tabuľku susedov
              vyplní, až keď s niekým komunikuje.
            </p>
          )}
        </Card>
      )}
    </section>
  );
}
